import axios from 'axios';
import { DateTime } from 'luxon';

function apply_auth(target) {
    target.interceptors.request.use(
        config => {
            config.headers['Authorization'] = `Bearer ${window.auth_token || 'dummy_token'}`;
            return config;
        },
        error => {
            Promise.reject(error);
        }
    );
    return target;
}

function handle_response(response) {
    return response.data;
}

// Retailer API

let baseURL = process.env.REACT_APP_TRADER_API_URL || 'http://localhost:4000/v1/retailer';
const client = apply_auth(axios.create({ baseURL }));

export function places() {
    return client.get('/facilities').then(handle_response);
}

export function place(facility_id) {
    return client.get(`/facilities/${facility_id}`).then(handle_response);
}

export function devices(facility_id) {
    return client.get(`/facilities/${facility_id}/devices`).then(handle_response);
}

export function device_insights(device_id) {
    return client.get(`/devices/${device_id}/insights`).then(handle_response);
}

export function stats() {
    return client.get('/devices/count/30').then(handle_response);
}

export function get_device_info(device_id) {
    return client.get(`/devices/${device_id}`).then(handle_response);
}

export function get_retailers_device_info(device_id) {
    return client.get(`/vbatt/devices/${device_id}`).then(handle_response);
}

export function get_device_life_events(device_id) {
    return client.get(`/devices/${device_id}/events`).then(handle_response);
}

export function get_recent_edap_samples(device_id) {
    return client.get(`/devices/${device_id}/recent-edap-samples`).then(handle_response);
}

export function get_historical_data(device_id, aggregateOn = 10) {
    let query = `/devices/${device_id}/historical-data?aggregateOn=${aggregateOn}`;
    return client.get(query).then(handle_response);
}

export function get_vbatt_devices() {
    return client.get(`/batteries/devices`).then(handle_response);
}

export function get_device_health_report(device_id) {
    return client.get(`/devices/${device_id}/health_report`).then(handle_response);
}

// Battery-level settled delivery reports, newest first, MW on the wire.
export function get_battery_delivered(battery_id, from, to, limit = 1000) {
    const query = `?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&limit=${limit}`;
    return client.get(`/batteries/${battery_id}/delivered${query}`).then(handle_response);
}

export function get_recent_delivery_report(device_id) {
    return client.get(`/devices/${device_id}/delivery-reports/recent-report`).then(handle_response);
}

export function get_devices_delivery_report(device_id, from, to) {
    let query = `/devices/${device_id}/delivery-reports`;

    if (from) {
        query += `?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
    }

    return client.get(query).then(handle_response);
}

export function customer_numbers(include_deleted, criteria, search) {
    let query = '/customers';

    if (include_deleted !== undefined) {
        query += `?include_deleted=${include_deleted}`;
    }

    if (criteria !== undefined) {
        query += `&criteria=${encodeURIComponent(criteria)}`;
    }

    if (search) {
        query += `&search=${encodeURIComponent(search)}`;
    }

    return client.get(query).then(handle_response);
}

export function customer_facilities(customer_id) {
    return client.get(`/customers/${customer_id}/facilities`).then(handle_response);
}

// The issued token embeds one entry per facility unless we opt out, which is the
// customer's entire address list. Past roughly ten facilities the link exceeds the
// 2 KB query-string limit enforced in front of the customer app and is refused with
// a bare 403 before it reaches us. omit_facilities keeps the link a constant size;
// the customer app reads the facilities from GET /facilities either way.
export function customer_web_app_link(customer_id, duration) {
    return client
        .get(`/customers/${customer_id}/web_app_link`, {
            params: {
                duration,
                omit_facilities: true,
            },
        })
        .then(handle_response);
}

export function onboarding_app_link(customer_id) {
    return client
        .get(`/customers/${customer_id}/onboarding_app_link/dummy`, {
            params: {
                omit_facilities: true,
            },
        })
        .then(handle_response);
}

export function web_app_settings() {
    return client.get('/web_app_settings').then(handle_response);
}

export function set_web_app_settings(settings) {
    return client.put('/web_app_settings', { settings });
}

export function get_market_operators() {
    return client.get('/market_operators').then(handle_response);
}

export function get_market_operator_price_areas(market_operator_name) {
    return client.get(`/market_operators/${market_operator_name}/price_areas`).then(handle_response);
}

export function get_spo_logs(device_id, from, to) {
    let query = `/spo_log/${device_id}`;
    if (from) {
        query += `?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
    }

    return client
        .get(query)
        .then(handle_response)
        .catch(e => console.error(e.message));
}

export function get_connect_logs(device_id, from, to) {
    let query = `/device_log/${device_id}`;
    if (from) {
        query += `?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
    }

    return client.get(query).then(handle_response);
}

export function get_devices_for_customer(customer_id, include_deleted) {
    let query = `/customers/${customer_id}/devices`;
    if (include_deleted) {
        query += `?include_deleted=${include_deleted}`;
    }

    return client.get(query).then(handle_response);
}

export function device_spo(device_id) {
    return client.get(`devices/${device_id}/spo`).then(handle_response);
}

export function delete_facility(facility_id) {
    return client.delete(`facilities/${facility_id}`).then(handle_response);
}

export function create_facility(customer_id, retailer_facility_id, address, price_area) {
    return client.post(`/customers/${customer_id}/facilities`, [{ retailer_facility_id, address, price_area }]).then(handle_response);
}

export function get_manufacturers() {
    return client.get('/settings/manufacturers').then(handle_response);
}

export function set_manufacturers(manufacturers) {
    return client.post('/settings/manufacturers', manufacturers).then(handle_response);
}

// Gateway onboardings accepted before the gateway was online (HTTP 202 from
// onboard_gateway), each with its customer and facility. `state` defaults to pending.
export function get_pending_gateway_onboardings(state) {
    return client.get('/gateways/pending-onboardings', { params: state ? { state } : {} }).then(handle_response);
}

export function get_gateways_among_devices(deviceIds) {
    return client.post('/gateways/by-device-ids', { device_ids: deviceIds }).then(handle_response);
}

export function get_device_gateway_health(deviceId) {
    return client.get(`/devices/${deviceId}/gateway/health`).then(handle_response);
}

export function get_device_gateway_metadata(deviceId) {
    return client.get(`/devices/${deviceId}/gateway/metadata`).then(handle_response);
}

export function set_device_gateway_battery_manufacturer(deviceId, battery_manufacturer) {
    return client.post(`/devices/${deviceId}/gateway/battery-manufacturer`, { battery_manufacturer }).then(handle_response);
}

// Sets the gateway's NAMEPLATE (gross) battery capacity in kWh. It takes precedence over
// whatever the inverter reports, until cleared.
export function set_device_gateway_battery_capacity(deviceId, battery_capacity_kwh) {
    return client.post(`/devices/${deviceId}/gateway/battery-capacity`, { battery_capacity_kwh }).then(handle_response);
}

// Clears the operator-supplied capacity, letting the inverter-reported figure govern again.
export function clear_device_gateway_battery_capacity(deviceId) {
    return client.delete(`/devices/${deviceId}/gateway/battery-capacity`).then(handle_response);
}

// Deletes a device — for gateway installations this is the "delete gateway" action:
// the backend releases gateway control (the gateway stops driving the inverter),
// soft-deletes the device, and the gateway drops out of the fleet views.
// Re-onboarding the serial is the path back. 204, no body.
export function delete_device(deviceId) {
    return client.delete(`/devices/${deviceId}`).then(handle_response);
}

export function get_device_gateway_health_reports(deviceId, from, to, limit = 1000, offset = 0) {
    return client.get(`/devices/${deviceId}/gateway/health_reports`, { params: { from, to, limit, offset } }).then(handle_response);
}

export function get_device_samples(deviceId, from, to) {
    let query = `/devices/${deviceId}/samples`;
    if (from) {
        query += `?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
    }
    return client.get(query).then(handle_response);
}

export function get_device_aggregated_samples(deviceId, from, to) {
    let query = `/devices/${deviceId}/aggregated-samples`;
    if (from) {
        query += `?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
    }
    return client.get(query).then(handle_response);
}

export function get_retailer_gateways(params = {}) {
    return client.get('/gateways', { params }).then(handle_response);
}

// Onboarded gateways aggregated per price area: counts plus summed battery
// capacity (kWh) and charge/discharge power (kW).
export function get_gateway_price_area_summary() {
    return client.get('/gateways/price-area-summary').then(handle_response);
}

export function get_gateway_fleet_overview(from, to) {
    return client.get('/gateway_health_reports/summary', { params: { from, to } }).then(handle_response);
}

export function get_gateway_alarm_frequency(from, to, limit = 10) {
    return client.get('/gateway_health_reports/alarms', { params: { from, to, limit } }).then(handle_response);
}

export function get_gateways_needing_attention() {
    return client.get('/gateway_health_reports/attention').then(handle_response);
}

export function get_gateway_health_by_brand(from, to) {
    return client.get('/gateway_health_reports/brands', { params: { from, to } }).then(handle_response);
}

export function get_gateway_alarm_devices(alarm_type, alarm_code, from, to) {
    return client
        .get(`/gateway_health_reports/alarms/${encodeURIComponent(alarm_code)}/gateways`, { params: { type: alarm_type, from, to } })
        .then(handle_response);
}

export function get_predefined_schedules() {
    return client.get('/predefined-schedules').then(handle_response);
}

export function get_schedule_devices(scheduleId) {
    return client.get(`/predefined-schedules/${scheduleId}/devices`).then(handle_response);
}

export function assign_schedule_to_device(scheduleId, deviceId) {
    return client.post(`/predefined-schedules/${scheduleId}/devices/${deviceId}`).then(handle_response);
}

export function unassign_device_from_schedule(scheduleId, deviceId) {
    return client.delete(`/predefined-schedules/${scheduleId}/devices/${deviceId}`).then(handle_response);
}

export async function get_device_follows_schedule(deviceId) {
    const { data } = await client.get(`/devices/${deviceId}/gateway/follow-schedule`).then(handle_response);
    return data.follow_schedule;
}

export function set_device_follows_schedule(deviceId, follow_schedule) {
    return client.post(`/devices/${deviceId}/gateway/follow-schedule`, { follow_schedule }).then(handle_response);
}

export function get_device_schedule(deviceId) {
    return client.get(`/devices/${deviceId}/gateway/schedule`).then(handle_response);
}

export function get_manufacturers_and_models(limit, offset, sortBy, sortDirection, manufacturer) {
    return client
        .get('/manufacturers_with_models', {
            params: {
                limit,
                offset,
                sortBy,
                sortDirection,
                manufacturer,
            },
            paramsSerializer: params => {
                const encodedParams = Object.keys(params)
                    .map(key => {
                        if (params[key]) {
                            return `${key}=${encodeURIComponent(params[key])}`;
                        }

                        return '';
                    })
                    .filter(param => param)
                    .join('&');
                return encodedParams;
            },
        })
        .then(handle_response);
}

export function geocode_facility(facility_id) {
    return client.post(`/facilities/${facility_id}/geocode`).then(handle_response);
}

export function patch_facility(facility_id, updates) {
    return client.patch(`/facilities/${facility_id}`, updates).then(handle_response);
}

export function onboard_gateway(facility_id, { serial_number, inverter_brand, price_area, battery_manufacturer, battery_capacity_kwh }) {
    return client
        .post(`/facilities/${facility_id}/onboard_gateway`, {
            serial_number,
            inverter_brand,
            price_area,
            // Optional — only forward when provided so we don't send an empty key.
            ...(battery_manufacturer ? { battery_manufacturer } : {}),
            ...(battery_capacity_kwh === undefined || battery_capacity_kwh === null ? {} : { battery_capacity_kwh }),
        })
        .then(handle_response);
}

export function graphQLUrl() {
    return baseURL + '/reporting';
}

export async function retailerReportingQuery(query, variables = {}) {
    let from = variables.from;
    let to = variables.to;
    if (from) {
        from = DateTime.utc(variables.from.getFullYear(), variables.from.getMonth() + 1, variables.from.getDate()).toJSDate();
    }

    if (to) {
        to = DateTime.utc(variables.to.getFullYear(), variables.to.getMonth() + 1, variables.to.getDate()).toJSDate();
    }

    variables = {
        ...variables,
        from,
        to,
    };

    let res = await client.post(`/reporting`, { query, variables });
    let body = res.data;
    if (body.errors) {
        console.error(body.errors);
        throw new Error(`GraphQL query failed: ${JSON.stringify(body.errors)}`);
    }
    return body.data;
}

// Base API

const baseClient = apply_auth(
    axios.create({
        baseURL: process.env.REACT_APP_BASE_API_URL || 'http://localhost:4000/v1',
    })
);

export function get_name_and_roles() {
    return baseClient.get('/whoami').then(response => {
        let body = response.data.data;

        const name = [body.retailer?.name, body.brp?.name].filter(x => x !== undefined).join(' | ');
        const roles = {
            retailer: new Set(body.retailer?.roles),
            brp: new Set(body.brp?.roles),
        };

        return {
            name,
            roles,
            // Guarded like `name` and `roles` above: a BRP-only account has no
            // `retailer` object, and reaching into it threw before the caller
            // could ever see the result.
            retailer: body.retailer?.id,
        };
    });
}

// API key management

const apiKeyClient = apply_auth(
    axios.create({
        baseURL: process.env.REACT_APP_API_KEY_URL || 'http://localhost:4000/v1/api-keys',
    })
);

export function api_keys(realm) {
    return apiKeyClient.get(`/${realm}`).then(handle_response);
}

export function revoke_api_key(realm, id) {
    return apiKeyClient.delete(`/${realm}/${id}`).then(handle_response);
}

export function upload_api_public_key(realm, name, roles, key) {
    return apiKeyClient.post(`/${realm}`, { name, roles, key }).then(handle_response);
}

export function generate_api_key_pair(realm, name, roles) {
    return apiKeyClient.post(`/${realm}`, { name, roles }).then(handle_response);
}

// User management
//
// Emails go into the path with encodeURIComponent, not encodeURI: `?` and `#`
// are legal in a local-part and encodeURI leaves both in place, which
// truncates the path at the first one.

const userManagementClient = apply_auth(
    axios.create({
        baseURL: process.env.REACT_APP_USER_MANAGEMENT_URL || 'http://localhost:4000/v1/users',
    })
);

export async function get_retailer_users() {
    return userManagementClient.get(`/retailer`).then(handle_response);
}

export async function get_retailer_roles() {
    return userManagementClient.get(`/roles/retailer`).then(handle_response);
}

export async function set_roles(email, role_list) {
    return userManagementClient.post(`/retailer/${encodeURIComponent(email)}/roles`, { role_list }).then(handle_response);
}

export async function reset_password(email) {
    return userManagementClient.post(`/retailer/${encodeURIComponent(email)}/password`).then(handle_response);
}

// Removes every OTP credential the user holds and ends their sessions; where
// MFA is enforced they are made to enrol again at the next login. Answers 204
// with no body, and 404 for a user outside the caller's own retailer.
export async function reset_mfa(email) {
    return userManagementClient.delete(`/retailer/${encodeURIComponent(email)}/mfa`).then(handle_response);
}

export async function create_user(email, firstname, lastname, roles) {
    return userManagementClient
        .post(`/retailer`, {
            email,
            firstname,
            lastname,
            roles,
        })
        .then(handle_response);
}

export async function delete_user(email) {
    return userManagementClient.delete(`/retailer/${encodeURIComponent(email)}`).then(handle_response);
}

export async function update_roles(email, roles) {
    return userManagementClient.post(`/retailer/${encodeURIComponent(email)}/roles`, roles).then(handle_response);
}

export async function is_mfa_enforced(realm) {
    return userManagementClient
        .get(`/mfa/${realm}`)
        .then(handle_response)
        .then(d => d.data.enabled);
}

export async function enforce_mfa(realm) {
    return userManagementClient.post(`/mfa/${realm}`);
}

export async function get_active_announcements() {
    return baseClient.get('/announcements/active').then(handle_response);
}

const exportedObject = {
    place,
    places,
    devices,
    stats,
    customer_numbers,
    customer_facilities,
    customer_web_app_link,
    onboarding_app_link,
    web_app_settings,
    set_web_app_settings,
    api_keys,
    revoke_api_key,
    upload_api_public_key,
    generate_api_key_pair,
    get_spo_logs,
    get_connect_logs,
    get_devices_for_customer,
    device_spo,
    delete_facility,
    create_facility,
    get_manufacturers,
    set_manufacturers,
    graphQLUrl,
    retailerReportingQuery,
    get_name_and_roles,
    get_retailer_users,
    get_retailer_roles,
    set_roles,
    reset_password,
    reset_mfa,
    create_user,
    delete_user,
    update_roles,
    is_mfa_enforced,
    enforce_mfa,
    get_battery_delivered,
    get_active_announcements,
    get_manufacturers_and_models,
    get_retailers_device_info,
    get_device_life_events,
    get_recent_edap_samples,
    get_historical_data,
    get_vbatt_devices,
    get_market_operators,
    get_market_operator_price_areas,
    get_device_health_report,
    get_recent_delivery_report,
    get_devices_delivery_report,
    get_device_info,
    get_gateways_among_devices,
    get_pending_gateway_onboardings,
    get_device_gateway_health,
    get_device_gateway_metadata,
    set_device_gateway_battery_manufacturer,
    set_device_gateway_battery_capacity,
    clear_device_gateway_battery_capacity,
    delete_device,
    get_device_gateway_health_reports,
    get_device_samples,
    get_device_aggregated_samples,
    get_retailer_gateways,
    get_gateway_price_area_summary,
    get_gateway_fleet_overview,
    get_gateway_alarm_frequency,
    get_gateway_alarm_devices,
    get_gateways_needing_attention,
    get_gateway_health_by_brand,
    get_predefined_schedules,
    assign_schedule_to_device,
    unassign_device_from_schedule,
    get_schedule_devices,
    get_device_follows_schedule,
    set_device_follows_schedule,
    geocode_facility,
    patch_facility,
    onboard_gateway,
};
export default exportedObject;
